/**
 * ImageProcessor
 * Canvas API helpers for crop, resize, and image processing
 * 
 * This module handles all low-level image manipulation using the HTML Canvas API.
 * All functions are designed to be memory-safe with proper URL cleanup.
 */

import { CropArea, ImageCropPosition, ValidationResult } from './types';
import { 
  ASPECT_RATIO_VALUES, 
  JPEG_QUALITY, 
  MAX_PREVIEW_WIDTH,
  ERROR_MESSAGES 
} from './constants';

// ============================================================================
// ASPECT RATIO UTILITIES
// ============================================================================

/**
 * Parse aspect ratio string to numeric value
 * Supports formats like "4:5", "16:9", "1.91:1"
 * 
 * @param aspectRatio - Aspect ratio string
 * @returns Numeric aspect ratio (width/height)
 */
export function parseAspectRatio(aspectRatio: string): number {
  // Check predefined values first for precision
  if (aspectRatio in ASPECT_RATIO_VALUES) {
    return ASPECT_RATIO_VALUES[aspectRatio as keyof typeof ASPECT_RATIO_VALUES];
  }
  
  // Parse custom aspect ratio
  const parts = aspectRatio.split(':');
  if (parts.length === 2) {
    const width = parseFloat(parts[0]);
    const height = parseFloat(parts[1]);
    if (!isNaN(width) && !isNaN(height) && height !== 0) {
      return width / height;
    }
  }
  
  // Default to 1:1 if parsing fails
  console.warn(`ImageProcessor: Failed to parse aspect ratio "${aspectRatio}", using 1:1`);
  return 1;
}

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Validate file size and type
 * 
 * @param file - File to validate
 * @param maxSize - Maximum file size in bytes
 * @param allowedTypes - Array of allowed MIME types
 * @returns Validation result with error message if invalid
 */
export function validateFile(
  file: File,
  maxSize: number,
  allowedTypes: string[]
): ValidationResult {
  if (!file) {
    return { valid: false, error: ERROR_MESSAGES.NO_FILE };
  }

  if (file.size > maxSize) {
    const maxMB = Math.round(maxSize / (1024 * 1024));
    return { 
      valid: false, 
      error: `Bestand is te groot (max ${maxMB}MB, dit bestand is ${(file.size / (1024 * 1024)).toFixed(1)}MB)` 
    };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: ERROR_MESSAGES.INVALID_TYPE 
    };
  }
  
  return { valid: true };
}

// ============================================================================
// IMAGE LOADING
// ============================================================================

/**
 * Load image from file with proper cleanup
 * 
 * @param file - Image file
 * @returns Promise resolving to { img: HTMLImageElement, objectUrl: string }
 * @note Caller is responsible for revoking the objectUrl when done
 */
export function loadImageFromFile(file: File): Promise<{ img: HTMLImageElement; objectUrl: string }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    
    img.onload = () => {
      resolve({ img, objectUrl });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
    };
    
    img.src = objectUrl;
  });
}

/**
 * Load image from file (legacy function for compatibility)
 * 
 * @param file - Image file
 * @returns Promise resolving to HTMLImageElement
 * @deprecated Use loadImageFromFile instead for proper cleanup tracking
 */
export function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      // Store objectUrl on img for cleanup
      (img as any).__objectUrl = objectUrl;
      resolve(img);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
    };
    
    img.src = objectUrl;
  });
}

// ============================================================================
// CROP CALCULATIONS
// ============================================================================

/**
 * Calculate center crop area for target aspect ratio
 * This ensures the image is properly centered when cropping
 * 
 * @param imageWidth - Source image width
 * @param imageHeight - Source image height
 * @param targetAspect - Target aspect ratio (width/height)
 * @returns Crop area coordinates for center crop
 */
export function calculateCenterCrop(
  imageWidth: number,
  imageHeight: number,
  targetAspect: number
): CropArea {
  const imageAspect = imageWidth / imageHeight;
  
  let cropWidth: number;
  let cropHeight: number;
  
  if (imageAspect > targetAspect) {
    // Image is wider than target - crop sides
    cropHeight = imageHeight;
    cropWidth = imageHeight * targetAspect;
  } else {
    // Image is taller than target - crop top/bottom
    cropWidth = imageWidth;
    cropHeight = imageWidth / targetAspect;
  }
  
  // Center the crop area
  const x = (imageWidth - cropWidth) / 2;
  const y = (imageHeight - cropHeight) / 2;
  
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(cropWidth),
    height: Math.round(cropHeight)
  };
}

export function calculatePositionedCrop(
  imageWidth: number,
  imageHeight: number,
  targetAspect: number,
  position: ImageCropPosition = { x: 50, y: 50 }
): CropArea {
  const centerCrop = calculateCenterCrop(imageWidth, imageHeight, targetAspect);
  const safeX = Math.max(0, Math.min(100, position.x));
  const safeY = Math.max(0, Math.min(100, position.y));

  if (imageWidth / imageHeight > targetAspect) {
    const maxX = Math.max(0, imageWidth - centerCrop.width);
    return {
      ...centerCrop,
      x: Math.round((maxX * safeX) / 100),
      y: 0,
    };
  }

  const maxY = Math.max(0, imageHeight - centerCrop.height);
  return {
    ...centerCrop,
    x: 0,
    y: Math.round((maxY * safeY) / 100),
  };
}

/**
 * Calculate the initial crop position for react-easy-crop
 * Returns percentage-based coordinates that center the image
 * 
 * @param imageWidth - Source image width
 * @param imageHeight - Source image height
 * @param targetAspect - Target aspect ratio
 * @returns Crop position as { x: 0, y: 0 } (always centered)
 */
export function calculateInitialCropPosition(
  imageWidth: number,
  imageHeight: number,
  targetAspect: number
): { x: number; y: number } {
  // react-easy-crop uses { x: 0, y: 0 } for center position
  return { x: 0, y: 0 };
}

// ============================================================================
// IMAGE PROCESSING
// ============================================================================

/**
 * Downsample image for faster processing
 * 
 * @param file - Original image file
 * @param maxWidth - Maximum width after downsampling
 * @returns Promise resolving to downsampled image as Blob
 */
export async function downsampleImage(
  file: File,
  maxWidth: number = MAX_PREVIEW_WIDTH
): Promise<Blob> {
  const { img, objectUrl } = await loadImageFromFile(file);
  
  try {
    // If image is already smaller, return original
    if (img.width <= maxWidth) {
      return file;
    }
    
    const scale = maxWidth / img.width;
    const newWidth = Math.round(img.width * scale);
    const newHeight = Math.round(img.height * scale);
    
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(ERROR_MESSAGES.CANVAS_ERROR);
    }
    
    // Enable high-quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, newWidth, newHeight);
    
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
          }
        },
        file.type === 'image/png' ? 'image/png' : 'image/jpeg',
        JPEG_QUALITY
      );
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Crop and resize image to target dimensions
 * 
 * @param imageSource - Image source (File, Blob, or URL)
 * @param cropArea - Crop area coordinates
 * @param targetWidth - Target output width
 * @param targetHeight - Target output height
 * @param outputType - Output MIME type
 * @returns Promise resolving to cropped image as Blob
 */
export async function cropAndResize(
  imageSource: File | Blob | string,
  cropArea: CropArea,
  targetWidth: number,
  targetHeight: number,
  outputType: string = 'image/jpeg'
): Promise<Blob> {
  let img: HTMLImageElement;
  let objectUrl: string | null = null;
  
  try {
    // Load image based on source type
    if (typeof imageSource === 'string') {
      img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
        img.src = imageSource;
      });
    } else {
      objectUrl = URL.createObjectURL(imageSource);
      img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
        img.src = objectUrl!;
      });
    }
    
    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(ERROR_MESSAGES.CANVAS_ERROR);
    }
    
    // Enable high-quality scaling
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Draw cropped and resized image
    ctx.drawImage(
      img,
      cropArea.x,
      cropArea.y,
      cropArea.width,
      cropArea.height,
      0,
      0,
      targetWidth,
      targetHeight
    );
    
    // Convert to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
          }
        },
        outputType,
        outputType === 'image/png' ? undefined : JPEG_QUALITY
      );
    });
  } finally {
    // Cleanup
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

/**
 * Generate auto-cropped variant with center crop
 * 
 * @param file - Original image file
 * @param aspectRatio - Target aspect ratio string
 * @param targetWidth - Target output width
 * @param targetHeight - Target output height
 * @returns Promise resolving to cropped image as Blob URL
 */
export async function generateAutoCropVariant(
  file: File,
  aspectRatio: string,
  targetWidth: number,
  targetHeight: number,
  cropPosition?: ImageCropPosition
): Promise<string> {
  const { img, objectUrl } = await loadImageFromFile(file);
  
  try {
    const targetAspect = parseAspectRatio(aspectRatio);
    const cropArea = cropPosition
      ? calculatePositionedCrop(img.width, img.height, targetAspect, cropPosition)
      : calculateCenterCrop(img.width, img.height, targetAspect);
    
    const blob = await cropAndResize(file, cropArea, targetWidth, targetHeight, file.type);
    return URL.createObjectURL(blob);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Generate a variant that keeps the full image visible and fills the canvas
 * with a blurred copy of the same image. Exported as compressed JPEG so the
 * blur is included when sharing to external platforms.
 */
export async function generateBlurContainVariant(
  file: File,
  aspectRatio: string,
  targetWidth: number,
  targetHeight: number
): Promise<string> {
  const { img, objectUrl } = await loadImageFromFile(file);

  try {
    parseAspectRatio(aspectRatio); // Keeps validation behavior aligned with other generators.

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(ERROR_MESSAGES.CANVAS_ERROR);
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const coverScale = Math.max(targetWidth / img.width, targetHeight / img.height);
    const coverWidth = img.width * coverScale;
    const coverHeight = img.height * coverScale;
    const coverX = (targetWidth - coverWidth) / 2;
    const coverY = (targetHeight - coverHeight) / 2;

    ctx.save();
    ctx.filter = 'blur(28px) brightness(0.82)';
    ctx.drawImage(img, coverX, coverY, coverWidth, coverHeight);
    ctx.restore();

    // Slight overlay keeps busy blurred backgrounds from competing with text-heavy media.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
    ctx.fillRect(0, 0, targetWidth, targetHeight);

    const containScale = Math.min(targetWidth / img.width, targetHeight / img.height);
    const containWidth = img.width * containScale;
    const containHeight = img.height * containScale;
    const containX = (targetWidth - containWidth) / 2;
    const containY = (targetHeight - containHeight) / 2;

    ctx.drawImage(img, containX, containY, containWidth, containHeight);

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error(ERROR_MESSAGES.PROCESSING_ERROR));
          }
        },
        'image/jpeg',
        JPEG_QUALITY
      );
    });

    return URL.createObjectURL(blob);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Generate custom-cropped variant from react-easy-crop output
 * 
 * @param file - Original image file
 * @param croppedAreaPixels - Crop area in pixels from react-easy-crop
 * @param targetWidth - Target output width
 * @param targetHeight - Target output height
 * @returns Promise resolving to cropped image as Blob URL
 */
export async function generateCustomCropVariant(
  file: File,
  croppedAreaPixels: CropArea,
  targetWidth: number,
  targetHeight: number
): Promise<string> {
  const blob = await cropAndResize(
    file, 
    croppedAreaPixels, 
    targetWidth, 
    targetHeight, 
    file.type
  );
  return URL.createObjectURL(blob);
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get image dimensions
 * 
 * @param file - Image file
 * @returns Promise resolving to { width, height }
 */
export async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const { img, objectUrl } = await loadImageFromFile(file);
  const dimensions = { width: img.width, height: img.height };
  URL.revokeObjectURL(objectUrl);
  return dimensions;
}

/**
 * Create object URL for preview
 * 
 * @param file - Image file
 * @returns Object URL string
 * @note Caller is responsible for cleanup via cleanupUrls()
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Cleanup object URLs to prevent memory leaks
 * Safe to call with undefined values in array
 * 
 * @param urls - Array of object URLs to revoke
 */
export function cleanupUrls(urls: (string | undefined | null)[]): void {
  urls.forEach(url => {
    if (url && typeof url === 'string' && url.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors if URL already revoked
      }
    }
  });
}

/**
 * Check if a URL is a blob URL
 * 
 * @param url - URL to check
 * @returns true if URL is a blob URL
 */
export function isBlobUrl(url: string | undefined | null): boolean {
  return typeof url === 'string' && url.startsWith('blob:');
}


























